import { inject } from '@angular/core';
import { Result, ResultStream } from '@causality/core';
import { FeedbackFormApi } from '@fmnts/api/next';
import { ConnectionService, hasConnectionStatus } from '@fmnts/common';
import {
  CommonError,
  GeneralHttpError,
  HttpErrorUtil,
} from '@formunauts/shared/errors/domain';
import { SupporterFeedbackForm } from '@formunauts/supporter-feedback/domain';
import * as Data from 'effect/Data';
import * as Fn from 'effect/Function';
import * as Option from 'effect/Option';
import { first } from 'rxjs';
import {
  SupporterFeedbackFormConflictError,
  SupporterFeedbackFormRepository,
  SupporterFeedbackFormSaveError,
} from './supporter-feedback-data-access.model';

export class SupporterFeedbackFormApiRepositoryConfig extends Data.Class<{
  timeout: number;
}> {}

export class SupporterFeedbackFormApiRepository extends SupporterFeedbackFormRepository {
  private readonly api = inject(FeedbackFormApi);
  private readonly connection = inject(ConnectionService);
  private readonly cfg = inject(SupporterFeedbackFormApiRepositoryConfig);

  override save(
    request: SupporterFeedbackForm.SaveRequest,
  ): ResultStream.ResultStream<
    SupporterFeedbackForm.SummaryForm,
    SupporterFeedbackFormSaveError
  > {
    return this.api.save(request.hash, request.data).pipe(
      ResultStream.fromObservable({
        onFailure: Fn.flow(
          GeneralHttpError.fromUnknown('fmnts'),
          _mapToSaveFeedbackFormError,
          Result.fail,
        ),
      }),
      ResultStream.failWhenOffline(this._isOnline$()),
      ResultStream.timeout({ each: this.cfg.timeout }),
    );
  }

  private _isOnline$() {
    return this.connection.connectionStatus$.pipe(
      hasConnectionStatus('online'),
      first(),
    );
  }
}

function _mapToSaveFeedbackFormError(
  e: Option.Option<GeneralHttpError>,
):
  | SupporterFeedbackFormConflictError
  | CommonError.UnexpectedError
  | CommonError.InfrastructureError {
  const _mapHttpError = (
    httpError: GeneralHttpError,
  ): Option.Option<
    CommonError.InfrastructureError | SupporterFeedbackFormConflictError
  > =>
    httpError.status === 409
      ? Option.some(new SupporterFeedbackFormConflictError())
      : HttpErrorUtil.getInfrastructureError(httpError);

  return Fn.pipe(
    e,
    Option.flatMap(_mapHttpError),
    Option.getOrElse(() => new CommonError.UnexpectedError()),
  );
}
